import React, { useState, useEffect } from 'react';
import { Alert, Row, Col, Form, Button, Card } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import PendingApplicantsTable from './components/PendingApplicantsTable';
import EscalationsTable from './components/EscalationsTable';
import EmailModal from './components/EmailModal';

const Notifications = () => {
  const [pendingApplicants, setPendingApplicants] = useState([]);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [pendingLimit] = useState(10);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [allFilteredIds, setAllFilteredIds] = useState([]);
  const [availableDomains, setAvailableDomains] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    domain: 'all'
  });

  const [emailTemplate, setEmailTemplate] = useState({
    subject: 'Invitation: Meet and Greet Session - Internship Opportunity',
    body: `Dear Candidate,
  
  Thank you for showing interest in our internship opportunity. We are delighted to invite you to a "Meet and Greet" session, where we look forward to learning more about you and sharing further details about the opportunity.
  
  Session Details:
  📅 Date: 27th June 2025
  ⏰ Time: 07:30 PM – 08:30 PM
  🔗 Zoom Link: https://us06web.zoom.us/j/87637708173?pwd=1bPAkFMcWheu2GGTIiTlxEHjPATSGy.1
  
  Action Required:
  Kindly share your updated CV with us at 📧 mandard@smartcookie.in before the session.
  
  For any queries or assistance regarding the meeting, feel free to contact us at 📞 6203057899
  
  More About the Projects:
  
  Smart Rewards: https://drive.google.com/file/d/1RfDjdPP0JRryE_FS2Iq59BNeLnAZ05zN/view?usp=sharing
  
  Continuous Job Network (CJN): https://drive.google.com/file/d/1RgxlOnB4xmBMnfKj4ZjP56f4QfiOEJEK/view?usp=sharing
  
  We look forward to connecting with you and discussing how you can be part of our exciting journey.
  
  Warm regards,
  Khushpreet Kaur
  HR Team
  Blue Planet Solutions`
  });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [flaggedApplicants, setFlaggedApplicants] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [escalationLoading, setEscalationLoading] = useState(false);
  const [escalationPage, setEscalationPage] = useState(1);
  const [escalationTotalPages, setEscalationTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications(1);
    fetchEscalations(1);
  }, []);

  const fetchNotifications = async (page = 1, customFilters = null) => {
    try {
      setLoading(true);
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const filtersToUse = customFilters || filters;

      const params = {
        page,
        limit: pendingLimit,
        ...(filtersToUse.dateFrom && { dateFrom: filtersToUse.dateFrom }),
        ...(filtersToUse.dateTo && { dateTo: filtersToUse.dateTo }),
        ...(filtersToUse.domain !== 'all' && { domain: filtersToUse.domain })
      };

      const response = await axios.get(`${base}/api/applicants/notifications`, {
        params,
        withCredentials: true
      });

      setPendingApplicants(response.data.pending || []);
      setPendingPage(response.data.pendingPagination?.page || 1);
      setPendingTotalPages(response.data.pendingPagination?.totalPages || 1);
      setPendingTotal(response.data.pendingPagination?.total || 0);
      setAllFilteredIds(response.data.allFilteredIds || []);
      setFlaggedApplicants(response.data.flagged || []);
      setAvailableDomains(response.data.domains || []);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchEscalations = async (page = 1) => {
    setEscalationLoading(true);
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const res = await axios.get(`${base}/api/applicants/escalations`, {
        params: { page },
        withCredentials: true
      });
      setEscalations(res.data.data || []);
      setEscalationPage(res.data.pagination.page);
      setEscalationTotalPages(res.data.pagination.totalPages);
    } catch (err) {
      toast.error('Failed to load escalations');
    } finally {
      setEscalationLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    setSelectedApplicants([]); // Clear selected applicants when applying new filters
    fetchNotifications(1, filters);
  };

  const clearFilters = () => {
    const clearedFilters = { dateFrom: '', dateTo: '', domain: 'all' };
    setFilters(clearedFilters);
    setSelectedApplicants([]);
    fetchNotifications(1, clearedFilters);
  };

  const handleSelectApplicant = (atsNumber, checked) => {
    if (checked) {
      setSelectedApplicants(prev => [...prev, atsNumber]);
    } else {
      setSelectedApplicants(prev => prev.filter(id => id !== atsNumber));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Select ALL filtered applicants if available
      if (allFilteredIds.length > 0) {
        setSelectedApplicants(allFilteredIds);
        toast.success(`Selected all ${allFilteredIds.length} applicants across all pages`);
      } else {
        // Fallback: select only visible ones
        setSelectedApplicants(pendingApplicants.map(a => a.ats_number));
        toast.info('Selected current page applicants');
      }
    } else {
      setSelectedApplicants([]);
    }
  };


  const handleSelectAllFiltered = () => {
    setSelectedApplicants(allFilteredIds);
    toast.success(`Selected all ${allFilteredIds.length} filtered applicants`);
  };

  const handlePendingPageChange = (page) => {
    setPendingPage(page);
    fetchNotifications(page);
  };

  const handleEscalationPageChange = (page) => {
    setEscalationPage(page);
    fetchEscalations(page);
  };

  const handleSendReminders = () => setShowEmailModal(true);

  const sendReminders = async () => {
    if (sendingEmails) return;
    try {
      if (selectedApplicants.length === 0) {
        toast.error('Please select applicants to send emails to');
        return;
      }
      setSendingEmails(true);
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const response = await axios.post(`${base}/api/applicants/notifications/send-reminders`, {
        atsNumbers: selectedApplicants,
        subject: emailTemplate.subject,
        body: emailTemplate.body,
      }, { withCredentials: true });

      // Handle different response scenarios
      const { summary, details } = response.data;

      if (summary.successful > 0) {
        toast.success(`✅ Successfully sent ${summary.successful} email(s)`);
      }

      if (summary.failed > 0) {
        toast.error(`❌ Failed to send ${summary.failed} email(s)`);
        console.error('Failed emails:', details.failed);
      }

      if (summary.skipped > 0) {
        toast.warning(`⚠️ Skipped ${summary.skipped} invalid email(s)`);
        console.warn('Skipped emails:', details.skipped);
      }

      setShowEmailModal(false);
      setSelectedApplicants([]);
      fetchNotifications();
    } catch (error) {
      console.error('Send reminders error:', error);
      if (error.response?.data) {
        const { summary, details } = error.response.data;
        if (summary) {
          toast.error(`Failed: ${summary.failed || 0}, Skipped: ${summary.skipped || 0}`);
        } else {
          toast.error('Failed to send reminders');
        }
      } else {
        toast.error('Failed to send reminders');
      }
    } finally {
      setSendingEmails(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>

      <Alert variant="info" className="mb-4">
        <h5>Pending Applicants</h5>
        <p className="mb-0">
          These applicants haven't attended any Meet & Greet session yet.
          You can send them reminders to join the next one. After 5 reminders, they'll be flagged.
        </p>
      </Alert>

      <h2 className="mb-4">Notifications & Follow-ups</h2>

      {/* Filters Section */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Filters</h5>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Application Date From</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Application Date To</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Domain</Form.Label>
                <Form.Select
                  value={filters.domain}
                  onChange={(e) => handleFilterChange('domain', e.target.value)}
                >
                  <option value="all">All Domains</option>
                  {availableDomains.map(domain => (
                    <option key={domain} value={domain}>{domain}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <div className="d-flex gap-2">
                <Button variant="primary" onClick={applyFilters}>
                  Apply Filters
                </Button>
                <Button variant="outline-secondary" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </Col>
          </Row>

          {/* Filter Summary and Select All Filtered */}
          {(filters.dateFrom || filters.dateTo || filters.domain !== 'all') && (
            <Row className="mt-3">
              <Col>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Active Filters:</strong>
                    {filters.dateFrom && <span className="badge bg-info ms-2">From: {filters.dateFrom}</span>}
                    {filters.dateTo && <span className="badge bg-info ms-2">To: {filters.dateTo}</span>}
                    {filters.domain !== 'all' && <span className="badge bg-info ms-2">Domain: {filters.domain}</span>}
                  </div>
                  {allFilteredIds.length > 0 && (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleSelectAllFiltered}
                      disabled={selectedApplicants.length === allFilteredIds.length}
                    >
                      Select All Filtered ({allFilteredIds.length})
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* <PendingApplicantsTable
        applicants={pendingApplicants}
        selectedApplicants={selectedApplicants}
        onSelectApplicant={handleSelectApplicant}
        onSelectAll={handleSelectAll}
        onSelectAllFiltered={handleSelectAllFiltered} // Add this line
        page={pendingPage}
        totalPages={pendingTotalPages}
        total={pendingTotal}
        onPageChange={handlePendingPageChange}
        onSendReminders={handleSendReminders}
        allFilteredCount={allFilteredIds.length}
      /> */}

      <PendingApplicantsTable
        applicants={pendingApplicants}
        selectedApplicants={selectedApplicants}
        onSelectApplicant={handleSelectApplicant}
        onSelectAll={handleSelectAll}
        onSelectAllFiltered={handleSelectAllFiltered}
        page={pendingPage}
        totalPages={pendingTotalPages}
        total={pendingTotal}
        onPageChange={handlePendingPageChange}
        onSendReminders={handleSendReminders}
        allFilteredCount={allFilteredIds.length}
      />

      <EscalationsTable
        escalations={escalations}
        loading={escalationLoading}
        page={escalationPage}
        totalPages={escalationTotalPages}
        onPageChange={handleEscalationPageChange}
      />

      <EmailModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        applicants={pendingApplicants}
        selectedApplicants={selectedApplicants}
        emailTemplate={emailTemplate}
        setEmailTemplate={setEmailTemplate}
        sendingEmails={sendingEmails}
        onSendEmails={sendReminders}
      />
    </div>
  );
};

export default Notifications;